import React, { useState, useMemo, useEffect } from 'react';
import { ImageOff, X, Download, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// ── Group images into monthly buckets for scannability ────────────────────
const monthKey = (iso) => {
  if (!iso) return 'Unknown';
  try {
    const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    if (isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  } catch { return 'Unknown'; }
};

const safeDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
};

const MediaTab = ({ messages, sessionNumber }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null); // index into flat images array

  // Extract image messages, newest-first
  const images = useMemo(() => (
    (messages || [])
      .filter(m => m.type === 'image' && m.content && !m.pending)
      .map(m => ({
        _id: m._id,
        url: m.content,
        sender: m.sender_name || 'Unknown',
        timestamp: m.timestamp,
      }))
      .sort((a, b) => {
        const da = safeDate(a.timestamp);
        const db = safeDate(b.timestamp);
        return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
      })
  ), [messages]);

  const openAt = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const next = () => setLightboxIdx(i => (i + 1) % images.length);
  const prev = () => setLightboxIdx(i => (i - 1 + images.length) % images.length);

  // Keyboard nav for lightbox: Esc closes, ← prev, → next
  useEffect(() => {
    if (lightboxIdx == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight' && images.length > 1) next();
      else if (e.key === 'ArrowLeft' && images.length > 1) prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, images.length]);

  // Group by month
  const groups = useMemo(() => {
    const acc = new Map();
    images.forEach((img, idx) => {
      const key = monthKey(img.timestamp);
      if (!acc.has(key)) acc.set(key, []);
      acc.get(key).push({ ...img, _flatIdx: idx });
    });
    return Array.from(acc.entries());
  }, [images]);

  const current = lightboxIdx != null ? images[lightboxIdx] : null;

  if (images.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 bg-[#e5ddd5]" data-testid="media-tab-empty">
        <ImageOff className="w-14 h-14 mb-3 opacity-40" />
        <p className="text-sm font-medium">No media yet</p>
        <p className="text-[11px] text-gray-400 mt-1">Photos shared in this session will appear here.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#e5ddd5]" data-testid="media-tab">
      {/* Header with count */}
      <div className="sticky top-0 z-10 bg-[#f0f2f5]/95 backdrop-blur border-b px-4 py-2 flex items-center justify-between text-xs text-gray-600">
        <span className="font-semibold">{images.length} {images.length === 1 ? 'photo' : 'photos'}</span>
        <span className="text-gray-400 text-[11px]">{sessionNumber}</span>
      </div>

      {/* Monthly grouped grid */}
      <div className="p-3 space-y-4">
        {groups.map(([label, items]) => (
          <div key={label}>
            <div className="flex items-center gap-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2 px-1">
              <Calendar className="w-3 h-3" />
              <span>{label}</span>
              <span className="text-gray-300">·</span>
              <span className="text-gray-400 font-medium normal-case">{items.length}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 md:grid-cols-6">
              {items.map(img => (
                <button
                  key={img._id}
                  onClick={() => openAt(img._flatIdx)}
                  className="relative aspect-square overflow-hidden rounded-md bg-gray-200 group focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-transform active:scale-95"
                  data-testid={`media-thumb-${img._id}`}
                >
                  <img
                    src={img.url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                  />
                  <div className="hidden absolute inset-0 items-center justify-center bg-gray-100 text-gray-400 flex-col gap-1">
                    <ImageOff className="w-4 h-4" />
                    <span className="text-[9px]">Unavailable</span>
                  </div>
                  {/* Sender overlay on hover */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[9px] font-semibold text-white truncate">{img.sender}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] flex flex-col"
          onClick={closeLightbox}
          data-testid="media-lightbox"
        >
          <div className="flex items-center justify-between px-4 py-3 text-white flex-shrink-0" onClick={e => e.stopPropagation()}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{current.sender}</p>
              <p className="text-[11px] text-gray-300">
                {(() => {
                  const d = safeDate(current.timestamp);
                  return d ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '';
                })()}
              </p>
            </div>
            <div className="flex gap-1">
              <a
                href={current.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="p-2 rounded-full hover:bg-white/10"
                title="Open original"
                data-testid="lightbox-open-original"
              >
                <Download className="w-5 h-5" />
              </a>
              <button
                onClick={closeLightbox}
                className="p-2 rounded-full hover:bg-white/10"
                data-testid="lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 relative" onClick={closeLightbox}>
            <img
              src={current.url}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={e => e.stopPropagation()}
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={e => { e.stopPropagation(); prev(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 rounded-full text-white"
                  data-testid="lightbox-prev"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); next(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 rounded-full text-white"
                  data-testid="lightbox-next"
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="text-center text-white/70 text-xs py-2" onClick={e => e.stopPropagation()}>
              {lightboxIdx + 1} of {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaTab;
